/**
 * Central display config for the PUBLIC pricing page (odinforge.io/pricing).
 *
 * Prices are the approved, VAT-inclusive, clean customer-facing numbers
 * (€0 / €20 / €50 / From €150) and mirror the authoritative billing config in
 * `packages/billing/src/plan-pricing.ts`. This module is kept dependency-light on
 * purpose so the public marketing bundle never pulls server-side billing code.
 *
 * Plan names, descriptions and feature lists otherwise track the established
 * pricing implementation — price, VAT wording, BALDER disclosure and the CTA were
 * updated, and the bot-ownership wording was corrected to match how the product
 * actually works: every plan gets ODIN's managed all-in-one bot connected for
 * them, and bringing your own bot identity is a Dedicated-only capability (see
 * `bot.runtime.dedicated` in packages/billing/src/plan-catalogue.ts). BALDER usage
 * is described as limited, never unlimited.
 */

export const VAT_INCLUDED_LABEL = "VAT included";
export const PRICING_GRID_NOTE =
  "All prices include VAT. Limited BALDER AI usage is included with each plan.";
export const BALDER_USAGE_EXPLANATION =
  "BALDER AI usage is subject to the monthly allowance included with your plan. Additional AI usage may be purchased separately. ODIN will display usage and pricing before additional charges are applied.";

export type PlanCode = "FREE" | "CORE" | "PRO" | "DEDICATED";

export interface PricingDisplayPlan {
  readonly code: PlanCode;
  readonly name: string;
  readonly price: string;
  readonly cadence: string;
  /** Whether to show the small "VAT included" label (paid plans only). */
  readonly vatIncluded: boolean;
  /** Customer-facing BALDER usage disclosure — never "unlimited". */
  readonly balderDisclosure: string;
  readonly ctaLabel: string;
  readonly ctaVariant: "primary" | "secondary";
  /** In-app route the CTA links to. Paid plans go to sign-in to subscribe. */
  readonly ctaTo: string;
  readonly featured: boolean;
  readonly desc: string;
  readonly features: readonly string[];
}

export const PRICING_PLANS: readonly PricingDisplayPlan[] = [
  {
    code: "FREE",
    name: "Free",
    price: "€0",
    cadence: "forever",
    vatIncluded: false,
    balderDisclosure: "Limited BALDER AI trial usage.",
    ctaLabel: "Start free",
    ctaVariant: "primary",
    ctaTo: "/login",
    featured: true,
    desc: "Everything you need to design and safely run one server.",
    features: [
      "One Discord server",
      "ODIN's bot, connected for you",
      "Blueprints & versioning",
      "Safe dry-run & approval",
      "BALDER AI assistant",
      "Tickets, polls & events",
    ],
  },
  {
    code: "CORE",
    name: "Core",
    price: "€20",
    cadence: "/month",
    vatIncluded: true,
    balderDisclosure: "Limited monthly BALDER AI usage included.",
    ctaLabel: "Choose Core",
    ctaVariant: "primary",
    ctaTo: "/login",
    featured: false,
    desc: "More capability and integrations for an active community.",
    features: [
      "Everything in Free",
      "n8n, RSS & streaming feeds",
      "Higher blueprint limits",
      "Priority guidance",
    ],
  },
  {
    code: "PRO",
    name: "Pro",
    price: "€50",
    cadence: "/month",
    vatIncluded: true,
    balderDisclosure: "Limited monthly BALDER AI usage included.",
    ctaLabel: "Choose Pro",
    ctaVariant: "primary",
    ctaTo: "/login",
    featured: false,
    desc: "The full toolkit for growing communities.",
    features: [
      "Everything in Core",
      "Advanced automations",
      "Extended history & exports",
      "Game-server notifications",
    ],
  },
  {
    code: "DEDICATED",
    name: "Dedicated",
    price: "From €150",
    cadence: "/month",
    vatIncluded: true,
    balderDisclosure: "Higher BALDER AI allowance included.",
    ctaLabel: "Contact us",
    ctaVariant: "secondary",
    ctaTo: "/contact",
    featured: false,
    desc: "Hands-on setup and extra support for teams.",
    features: [
      "Everything in Pro",
      "Bring your own bot identity",
      "Tailored onboarding",
      "Direct support channel",
      "Help migrating an existing server",
    ],
  },
];
